import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const ContactPage = () => {
  const formRef = useRef(null);
  const isInView = useInView(formRef, { once: true, amount: 0.3 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'İsim gerekli';
    if (!formData.email) {
      newErrors.email = 'E-posta gerekli';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta giriniz';
    }
    if (!formData.subject) newErrors.subject = 'Konu gerekli';
    if (!formData.message) newErrors.message = 'Mesaj gerekli';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      
      // Simulate form submission
      setTimeout(() => {
        setIsSubmitting(false);
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
        });
      }, 1500);
    }
  };

  const contactInfoVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-secondary-light py-12"
    >
      <div className="container-custom">
        <h1 className="text-3xl font-bold text-gray-dark mb-8">İletişim</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <motion.div
            className="lg:col-span-1"
            variants={contactInfoVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="bg-white rounded-lg shadow-medium p-6">
              <motion.h2 
                className="text-xl font-medium text-gray-dark mb-6"
                variants={itemVariants}
              >
                İletişim Bilgileri
              </motion.h2>
              
              <motion.div 
                className="space-y-6"
                variants={contactInfoVariants}
              >
                <motion.div 
                  className="flex items-start"
                  variants={itemVariants}
                >
                  <div className="mr-4 p-2 bg-primary/10 text-primary rounded-full">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-dark">Adres</h3>
                    <address className="not-italic text-gray mt-1">
                    Akmescit, Terakki Sk NO:3/A, 03200 Merkez/Afyonkarahisar
                    </address>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex items-start"
                  variants={itemVariants}
                >
                  <div className="mr-4 p-2 bg-primary/10 text-primary rounded-full">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-dark">Telefon</h3>
                    <p className="text-gray mt-1">+90 0542 893 07 19</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex items-start"
                  variants={itemVariants}
                >
                  <div className="mr-4 p-2 bg-primary/10 text-primary rounded-full">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-dark">E-posta</h3>
                    <p className="text-gray mt-1">selimcelikafyon@gmail.com</p>
                  </div>
                </motion.div>
              </motion.div>
              
              <motion.div 
                className="mt-8 p-4 bg-primary/5 rounded-lg"
                variants={itemVariants}
              >
                <h3 className="font-medium text-gray-dark mb-2">Çalışma Saatleri</h3>
                <p className="text-gray">Pazartesi - Cumartesi: 09:00 - 18:00</p>
                <p className="text-gray">Pazar: 12:00 - 17:00</p>
              </motion.div>
            </div>
          </motion.div>
          
          {/* Contact Form */}
          <div className="lg:col-span-2" ref={formRef}>
            <div className="bg-white rounded-lg shadow-medium p-6">
              <motion.h2 
                className="text-xl font-medium text-gray-dark mb-6"
                initial={{ opacity: 0, y: -20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
              >
                Bize Mesaj Gönderin
              </motion.h2>
              
              {isSubmitted ? (
                <motion.div 
                  className="bg-green-50 text-green-800 p-4 rounded-md"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <h3 className="text-lg font-medium mb-2">Mesajınız İletildi!</h3>
                  <p>En kısa sürede size dönüş yapacağız. Teşekkür ederiz.</p>
                </motion.div>
              ) : (
                <motion.form 
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-gray-dark mb-1">İsim</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md focus:outline-none focus:ring-1 ${errors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                      />
                      {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-gray-dark mb-1">E-posta</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md focus:outline-none focus:ring-1 ${errors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-gray-dark mb-1">Konu</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`w-full p-2 border rounded-md focus:outline-none focus:ring-1 ${errors.subject ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                    />
                    {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-gray-dark mb-1">Mesaj</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className={`w-full p-2 border rounded-md focus:outline-none focus:ring-1 ${errors.message ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                    />
                    {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="btn-primary flex items-center"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
                      {!isSubmitting && <Send size={18} className="ml-2" />}
                    </button>
                  </div>
                </motion.form>
              )}
            </div>
          </div>
        </div>
        
        {/* Map */}
        <div className="mt-8 rounded-lg overflow-hidden shadow-medium">
  <div className="aspect-[16/9] bg-gray-200 flex items-center justify-center">
    <iframe
      title="Çelik Ticaret Harita"
      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3111.4073654421018!2d30.536578900000002!3d38.7543597!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cf17fab85aeafd%3A0xfde17971e655c500!2zw4dFTMSwSyBUT1BUQU4gSEFDIE1BTFpFTUVMRVLEsCAmIFRVSEFGxLBZRSAmIEzDnFggSElSREFWQVQ!5e0!3m2!1str!2str!4v1745601678433!5m2!1str!2str"
      width="1600"
      height="700"
      style={{ border: 0 }}
      allowFullScreen=""
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    ></iframe>
  </div>
</div>

      </div>
    </motion.div>
  );
};

export default ContactPage;